import Image from 'next/image'
import clsx from 'clsx'

/* blocks */
import Link from '@/atoms/Link/Link'

/* utils */
import { dataLayerPostClickEvent } from '@/utils/functions/dataLayerEvents'

/* assets */
import { ReactComponent as ChevronRightIcon } from 'public/assets/icons/chevron-right-bold.svg'
import { ReactComponent as ChevronRightWhiteIcon } from 'public/assets/icons/chevron-right-white.svg'

interface CaseStudyCardProps {
  className?: string
  data: {
    slug: string
    heroTitle: string
    caseStudyCardBgImage: StrapiImageFile
    customerLogo: StrapiImageFile
    statisticsList: StrapiBlockComponent[]
  }[]
  isHomePage?: boolean
}

function NormalCard(props: CaseStudyCardProps) {
  return (
    <>
      {props.data.map((item, index) => (
        <Link
          key={item.slug}
          href={`/case-study/${item.slug}/`}
          onClick={() =>
            dataLayerPostClickEvent({
              title: props.data[index].heroTitle,
              type: 'case-study',
              url: `/case-study/${props.data[index].slug}`
            })
          }
          className={clsx(
            'bg-white font-heading | flex flex-col | rounded-2xl',
            props.className
          )}
        >
          {item.customerLogo ? (
            <div className="relative max-h-72 mb-5 md:mb-8">
              {item.caseStudyCardBgImage ? (
                <Image
                  src={item.caseStudyCardBgImage.url}
                  alt={item.caseStudyCardBgImage.alternativeText ?? ''}
                  height={item.caseStudyCardBgImage.height}
                  width={item.caseStudyCardBgImage.width}
                  loading="lazy"
                  className="object-cover rounded-2xl h-full"
                />
              ) : (
                <div className="h-72" />
              )}

              <Image
                src={item.customerLogo.url}
                alt={item.customerLogo.alternativeText ?? ''}
                height={item.customerLogo.height}
                width={item.customerLogo.width}
                loading="lazy"
                className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-32"
              />
            </div>
          ) : null}
          <h2 className="text-2xl md:text-3xl text-blue-web-90 font-semibold | line-clamp-2 h-14 md:h-22  |  mb-5 md:mb-7">
            {item.heroTitle}
          </h2>
          <span className="text-xl text-blue-web-90 | flex items-center gap-2">
            Read Case Study
            <div className="border border-blue-web-90 p-2 rounded-full">
              <ChevronRightIcon />
            </div>
          </span>
        </Link>
      ))}
    </>
  )
}

function FullCard(props: CaseStudyCardProps) {
  return (
    <>
      {props.data.map((item, index) => (
        <Link
          key={item.slug}
          href={`/case-study/${item.slug}/`}
          onClick={() =>
            dataLayerPostClickEvent({
              title: props.data[index].heroTitle,
              type: 'case-study',
              url: `/case-study/${props.data[index].slug}`
            })
          }
          className={clsx(
            'bg-white font-heading | flex flex-col | rounded-2xl',
            props.className
          )}
        >
          {item.customerLogo ? (
            <div className="relative">
              {item.caseStudyCardBgImage ? (
                <Image
                  src={item.caseStudyCardBgImage.url}
                  alt={item.caseStudyCardBgImage.alternativeText ?? ''}
                  height={item.caseStudyCardBgImage.height}
                  width={item.caseStudyCardBgImage.width}
                  className="object-cover rounded-2xl"
                />
              ) : null}
              <div className="text-white | absolute top-0 | h-full p-5 md:p-7">
                <div className="h-22 md:h-24 lg:h-36">
                  <Image
                    src={item.customerLogo.url}
                    alt={item.customerLogo.alternativeText ?? ''}
                    height={item.customerLogo.height}
                    width={item.customerLogo.width}
                    className="w-28 md:w-32"
                  />
                </div>
                <h2 className="text-xl font-semibold | line-clamp-4 h-24 mb-5 md:mb-8 lg:mb-4">
                  {item.heroTitle}
                </h2>
                <hr className="text-white | mb-4 lg:mb-2" />
                <div className="flex gap-4 md:gap-6 | mb-9 md:mb-7 h-20">
                  {item.statisticsList.slice(0, 2).map(stat => (
                    <div key={stat.id}>
                      <div
                        className="text-3xl | md:mb-2"
                        dangerouslySetInnerHTML={{ __html: stat.title ?? '' }}
                      />
                      <div
                        className="font-body text-caption h-7"
                        dangerouslySetInnerHTML={{
                          __html: stat.description ?? ''
                        }}
                      />
                    </div>
                  ))}
                </div>
                <div className="text-base font-semibold text-blue-web-90  |  flex items-center gap-2">
                  Read Case Study
                  <div className="bg-blue-web-90 p-2 rounded-full">
                    <ChevronRightWhiteIcon />
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </Link>
      ))}
    </>
  )
}

function CaseStudyCard(props: CaseStudyCardProps) {
  if (props.isHomePage) return <FullCard {...props} />
  return <NormalCard {...props} />
}

export default CaseStudyCard
